import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';
import useScoringStatus from './useScoringStatus';
import { ScoringChip } from './ScoringStatus';
import { TriStateValue, ListChips, DetailRow, petsSummary } from './ListingFieldControls';

export default function PropertyEvaluation() {
  const { propertyId, buyerId } = useParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  const [property, setProperty] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [fitScore, setFitScore] = useState(null);
  const [grades, setGrades] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [progress, setProgress] = useState(null);
  const [aiReport, setAiReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [propertyId, buyerId]);

  // "Not yet scored" is ambiguous while a background job is in flight — distinguish
  // scoring / failed / genuinely unscored, and reload once the queue drains.
  const { buyerState, propertyState, failedJob, retry: retryScoring } =
    useScoringStatus({ onSettled: () => loadData() });
  const scoringPending = buyerState(buyerId) || propertyState(propertyId);
  const failedScoringJob = failedJob('buyer', buyerId) || failedJob('property', propertyId);

  const loadData = async () => {
    try {
      const [propRes, buyerRes, fitRes, gradesRes, hierRes, progressRes, overridesRes, notesRes] = await Promise.all([
        authFetch(`${API}/api/properties/${propertyId}`),
        authFetch(`${API}/api/buyers/${buyerId}`),
        authFetch(`${API}/api/grades/fit-score/${propertyId}/buyer/${buyerId}`).catch(() => null),
        authFetch(`${API}/api/grades/property/${propertyId}/buyer/${buyerId}`),
        authFetch(`${API}/api/criteria/buyer/${buyerId}/hierarchy`),
        authFetch(`${API}/api/grades/progress/${propertyId}/buyer/${buyerId}`),
        authFetch(`${API}/api/overrides/${propertyId}/buyer/${buyerId}`).catch(() => null),
        authFetch(`${API}/api/notes/${propertyId}/buyer/${buyerId}`).catch(() => null),
      ]);
      if (propRes.ok) setProperty(await propRes.json());
      if (buyerRes.ok) setBuyer(await buyerRes.json());
      if (fitRes && fitRes.ok) setFitScore(await fitRes.json());
      if (gradesRes.ok) setGrades(await gradesRes.json());
      if (hierRes.ok) setHierarchy((await hierRes.json()).criteria || []);
      if (progressRes.ok) setProgress(await progressRes.json());
      if (overridesRes && overridesRes.ok) {
        const oData = await overridesRes.json();
        const oMap = {};
        oData.forEach(o => { oMap[o.criterion_id] = o; });
        setOverrides(oMap);
      }
      if (notesRes && notesRes.ok) setNotes(await notesRes.json());
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGrade = async (criterionId, grade) => {
    try {
      await authFetch(`${API}/api/grades`, {
        method: 'POST',
        body: JSON.stringify({
          property_id: parseInt(propertyId),
          buyer_id: parseInt(buyerId),
          criterion_id: criterionId,
          grade: grade,
        })
      });
      loadData();
    } catch (err) {
      console.error('Grade error:', err);
    }
  };

  const handleOverride = async (criterionId, grade) => {
    try {
      await authFetch(`${API}/api/overrides`, {
        method: 'POST',
        body: JSON.stringify({
          property_id: parseInt(propertyId),
          buyer_id: parseInt(buyerId),
          criterion_id: criterionId,
          grade: grade,
        })
      });
      loadData();
    } catch (err) {
      console.error('Override error:', err);
    }
  };

  const removeOverride = async (criterionId) => {
    try {
      await authFetch(`${API}/api/overrides/${propertyId}/buyer/${buyerId}/criterion/${criterionId}`, {
        method: 'DELETE'
      });
      loadData();
    } catch (err) {
      console.error('Remove override error:', err);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const res = await authFetch(`${API}/api/notes`, {
        method: 'POST',
        body: JSON.stringify({
          property_id: parseInt(propertyId),
          buyer_id: parseInt(buyerId),
          note_text: newNote.trim(),
        })
      });
      if (res.ok) {
        setNewNote('');
        loadData();
      }
    } catch (err) {
      console.error('Add note error:', err);
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await authFetch(`${API}/api/notes/${noteId}`, { method: 'DELETE' });
      loadData();
    } catch (err) {
      console.error('Delete note error:', err);
    }
  };

  const generateAiReport = async () => {
    setGeneratingReport(true);
    try {
      const res = await authFetch(`${API}/api/ai/${propertyId}/buyer/${buyerId}/report`);
      if (res.ok) {
        const data = await res.json();
        setAiReport(data.report);
      }
    } catch (err) {
      console.error('AI report error:', err);
    } finally {
      setGeneratingReport(false);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      'ELITE FIT': 'text-[#00E676]',
      'STRONG FIT': 'text-[#00D4FF]',
      'GOOD FIT': 'text-blue-400',
      'MARGINAL FIT': 'text-yellow-400',
      'POOR FIT': 'text-red-400',
    };
    return colors[tier] || 'text-gray-400';
  };

  const getGradeColor = (grade) => {
    if (grade >= 8) return 'bg-green-600';
    if (grade >= 7) return 'bg-cyan-600';
    if (grade >= 6) return 'bg-blue-600';
    if (grade >= 4) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  const formatPrice = (price) => price ? `$${Number(price).toLocaleString()}` : 'N/A';
  const getScorePercent = (score) => score ? Math.round((score / 9) * 100) : 0;

  const getExistingGrade = (criterionId) => {
    const g = grades.find(g => g.criterion_id === criterionId);
    return g ? g.grade : null;
  };

  const allTabs = [
    { id: 'details', label: 'Property Details' },
    { id: 'evaluation', label: 'Evaluation' },
    { id: 'grading', label: 'Grading' },
  ];
  const tabs = (user?.role === 'super_admin' || user?.role === 'buyer')
    ? allTabs
    : allTabs.filter(t => t.id === 'details' || t.id === 'evaluation');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading evaluation...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-red-400 text-lg">Property not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-6">
          <Link to={`/match-board?buyer=${buyerId}`} className="text-[#00D4FF] text-sm hover:text-[#00E676] transition-colors mb-2 inline-block">
            ← Back to Match Board
          </Link>
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3 md:gap-0">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white">{property.address}</h1>
              <p className="text-gray-400">{property.city}, {property.state} {property.zip}</p>
              <p className="text-[#B0BEC5] text-sm mt-1">Client: <span className="text-[#00D4FF] font-medium">{buyer?.name}</span></p>
            </div>
            <div className="text-right">
              {fitScore ? (
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-white">{getScorePercent(fitScore.overall)}%</div>
                  <div className={`text-lg font-semibold ${getTierColor(fitScore.tier)}`}>{fitScore.tier}</div>
                  {progress && (
                    <div className="text-xs text-gray-500 mt-1">{progress.graded}/{progress.total} criteria graded</div>
                  )}
                </div>
              ) : scoringPending === 'scoring' ? (
                <div className="text-[#00D4FF] text-sm">Scoring…</div>
              ) : scoringPending === 'failed' ? (
                <div className="flex flex-col items-end gap-1">
                  <div className="text-red-400 text-sm">Scoring failed</div>
                  <ScoringChip
                    state="failed"
                    failedJob={failedScoringJob}
                    onRetry={retryScoring}
                  />
                </div>
              ) : (
                <div className="text-gray-500">Not yet scored</div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 border-b border-[#546E7A]/30">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'text-[#00D4FF] border-[#00D4FF]'
                  : 'text-gray-400 border-transparent hover:text-white hover:border-[#546E7A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB: Property Details */}
        {activeTab === 'details' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
              <h3 className="text-white font-semibold mb-4">Property Information</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Price:</span><br /><span className="text-[#00E676] font-bold text-lg">{formatPrice(property.price)}</span></div>
                <div><span className="text-gray-500">Price/Sqft:</span><br /><span className="text-white">{property.sqft && property.price ? `$${Math.round(property.price / property.sqft)}` : 'N/A'}</span></div>
                <div><span className="text-gray-500">Bedrooms:</span><br /><span className="text-white">{property.beds || 'N/A'}</span></div>
                <div><span className="text-gray-500">Bathrooms:</span><br /><span className="text-white">{property.baths || 'N/A'}</span></div>
                <div><span className="text-gray-500">Square Feet:</span><br /><span className="text-white">{property.sqft ? property.sqft.toLocaleString() : 'N/A'}</span></div>
                <div><span className="text-gray-500">Lot Size:</span><br /><span className="text-white">{property.lot_size_acres ? `${property.lot_size_acres} acres` : 'N/A'}</span></div>
                <div><span className="text-gray-500">Year Built:</span><br /><span className="text-white">{property.year_built || 'N/A'}</span></div>
                <div><span className="text-gray-500">Garage:</span><br /><span className="text-white">{property.garage_spaces ? `${property.garage_spaces} spaces` : 'N/A'}</span></div>
                <div><span className="text-gray-500">HOA:</span><br /><span className="text-white">{property.hoa_monthly ? `$${property.hoa_monthly}/month` : 'None'}</span></div>
                <div><span className="text-gray-500">Type:</span><br /><span className="text-white">{property.property_type?.replace('_', ' ') || 'N/A'}</span></div>
              </div>
            </div>
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
              <h3 className="text-white font-semibold mb-4">Location & School</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">School District:</span><br /><span className="text-white">{property.school_district || 'N/A'}</span></div>
                <div><span className="text-gray-500">School Rating:</span><br /><span className="text-white">{property.school_rating ? `${property.school_rating}/10` : 'N/A'}</span></div>
                <div><span className="text-gray-500">Status:</span><br /><span className={`font-medium ${property.listing_status === 'active' ? 'text-green-400' : property.listing_status === 'pending' ? 'text-yellow-400' : 'text-red-400'}`}>{property.listing_status}</span></div>
                <div><span className="text-gray-500">MLS #:</span><br /><span className="text-white">{property.mls_number || 'N/A'}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm mt-3 pt-3 border-t border-[#546E7A]/20">
                <DetailRow label="Elementary" value={property.elementary_school} />
                <DetailRow label="Middle" value={property.middle_school} />
                <DetailRow label="High" value={property.high_school} />
                <DetailRow label="Subdivision" value={property.subdivision} />
                <DetailRow label="County" value={property.county} />
              </div>
              {property.listing_url && (
                <a href={property.listing_url} target="_blank" rel="noopener noreferrer"
                  className="inline-block mt-4 px-4 py-2 bg-[#243B55] text-[#00D4FF] rounded-lg text-sm font-medium hover:bg-[#2d4a6a] transition-colors">
                  View Full MLS Listing →
                </a>
              )}
            </div>
            {/* Expanded listing attributes. Booleans show Yes / No / Not stated, because a
                listing that never mentioned a pool is not the same as one that says "no pool"
                — and only the latter counts against it when scored. */}
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6 md:col-span-2">
              <h3 className="text-white font-semibold mb-4">Features & Community</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <DetailRow label="Pool" showEmpty><TriStateValue value={property.pool} /></DetailRow>
                <DetailRow label="Waterfront" showEmpty><TriStateValue value={property.waterfront} /></DetailRow>
                <DetailRow label="Gated" showEmpty><TriStateValue value={property.gated_community} /></DetailRow>
                <DetailRow label="Senior (55+)" showEmpty><TriStateValue value={property.senior_community} /></DetailRow>
                <DetailRow label="Accessibility" showEmpty><TriStateValue value={property.accessibility_features} /></DetailRow>
                <DetailRow label="Stories" value={property.stories} showEmpty />
                <DetailRow label="Full baths" value={property.bathrooms_full} showEmpty />
                <DetailRow label="Half baths" value={property.bathrooms_half} showEmpty />
                <DetailRow label="Pets" value={petsSummary(property)} showEmpty />
                <DetailRow label="Furnished" value={property.furnished} showEmpty />
              </div>
              {(property.pool_features || property.waterfront_features || property.waterfront_type) && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {property.pool_features && (
                    <div><span className="text-gray-500 text-xs">Pool features</span>
                      <div className="mt-1"><ListChips value={property.pool_features} /></div></div>
                  )}
                  {(property.waterfront_features || property.waterfront_type) && (
                    <div><span className="text-gray-500 text-xs">Waterfront features</span>
                      <div className="mt-1"><ListChips value={property.waterfront_features || property.waterfront_type} /></div></div>
                  )}
                </div>
              )}
              {(property.appliances || property.community_amenities) && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {property.appliances && (
                    <div><span className="text-gray-500 text-xs">Appliances included</span>
                      <div className="mt-1"><ListChips value={property.appliances} /></div></div>
                  )}
                  {property.community_amenities && (
                    <div><span className="text-gray-500 text-xs">Community amenities</span>
                      <div className="mt-1"><ListChips value={property.community_amenities} tone="green" /></div></div>
                  )}
                </div>
              )}
              {property.description && (
                <div className="mt-4 pt-3 border-t border-[#546E7A]/20">
                  <span className="text-gray-500 text-xs">Listing description</span>
                  <p className="text-gray-200 text-sm mt-1 whitespace-pre-wrap leading-relaxed">{property.description}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: Evaluation (Fit Score + AI Report) */}
        {activeTab === 'evaluation' && (
          <div className="space-y-6">
            {/* Dimension Scores */}
            {fitScore && fitScore.dimension_scores && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Category Scores</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
                  {Object.entries(fitScore.dimension_scores).map(([name, score]) => (
                    <div key={name} className="text-center p-3 bg-[#0A0E27] rounded-lg">
                      <div className="text-2xl md:text-3xl font-bold text-white">{getScorePercent(score)}%</div>
                      <div className="text-xs text-gray-400 mt-1">{name}</div>
                      <div className="w-full bg-[#546E7A]/30 rounded-full h-1.5 mt-2">
                        <div className={`h-1.5 rounded-full ${getGradeColor(score)}`} style={{ width: `${(score / 9) * 100}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Strengths & Concerns */}
            {fitScore && fitScore.highlights && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#1A2744] rounded-xl border border-green-600/20 p-4 md:p-6">
                  <h3 className="text-[#00E676] font-semibold mb-3">Strengths</h3>
                  {(fitScore.highlights.strengths || fitScore.strengths || []).length > 0 ? (
                    <div className="space-y-2">
                      {(fitScore.highlights.strengths || fitScore.strengths || []).map((s, i) => (
                        <div key={i} className="flex justify-between gap-2 text-sm">
                          <span className="text-gray-300 min-w-0 break-words">{s.name}</span>
                          <span className="text-[#00E676] font-medium shrink-0">{getScorePercent(s.score)}%</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">None to report</p>
                  )}
                </div>
                <div className="bg-[#1A2744] rounded-xl border border-red-600/20 p-4 md:p-6">
                  <h3 className="text-red-400 font-semibold mb-3">Concerns</h3>
                  {(fitScore.highlights.concerns || fitScore.concerns || []).length > 0 ? (
                    <div className="space-y-2">
                      {(fitScore.highlights.concerns || fitScore.concerns || []).map((c, i) => (
                        <div key={i} className="flex justify-between gap-2 text-sm">
                          <span className="text-gray-300 min-w-0 break-words">{c.name}</span>
                          <span className="text-red-400 font-medium shrink-0">{getScorePercent(c.score)}%</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">None to report</p>
                  )}
                </div>
              </div>
            )}

            {/* Buyer Notes */}
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                <div className="min-w-0">
                  <h3 className="text-white font-semibold">Showing Notes</h3>
                  <p className="text-gray-500 text-xs mt-0.5">{user?.role === 'buyer' ? 'How do you feel about this property? Your notes (and your agent\'s) help the AI write a more personal report.' : `Notes from ${buyer?.name || 'your buyer'} and you — used by the AI report for personalized evaluation.`}</p>
                </div>
                <span className="text-gray-600 text-xs shrink-0">{notes.length} note{notes.length !== 1 ? 's' : ''}</span>
              </div>

              {/* Existing Notes */}
              {notes.length > 0 && (
                <div className="space-y-2 mb-4">
                  {notes.map(note => (
                    <div key={note.id} className="flex justify-between items-start p-3 bg-[#0A0E27]/50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-300 text-sm whitespace-pre-wrap">{note.note_text}</p>
                        <p className="text-gray-600 text-xs mt-1">
                          {note.author_name && (
                            <span className={note.author_role === 'buyer' ? 'text-cyan-400/80' : 'text-green-400/80'}>
                              {note.author_name} · {note.author_role === 'buyer' ? 'Buyer' : note.author_role === 'broker' ? 'Broker' : note.author_role === 'super_admin' ? 'Admin' : 'Agent'} ·{' '}
                            </span>
                          )}
                          {new Date(String(note.created_at).replace(' ', 'T')).toLocaleDateString()}
                        </p>
                      </div>
                      {note.can_edit && (
                        <button onClick={() => deleteNote(note.id)}
                          className="text-gray-600 hover:text-red-400 text-sm ml-3 shrink-0 transition-colors" title="Delete note">✕</button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Note — the buyer, their agent/broker, or an admin */}
              {['buyer', 'agent', 'broker', 'super_admin'].includes(user?.role) && (
                <div className="flex gap-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Loved the backyard, kitchen felt small, neighborhood was quiet on a Saturday..."
                    className="flex-1 px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] resize-none"
                    rows={2}
                  />
                  <button
                    onClick={addNote}
                    disabled={savingNote || !newNote.trim()}
                    className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors text-sm self-end"
                  >
                    {savingNote ? '...' : 'Add'}
                  </button>
                </div>
              )}
            </div>

            {/* AI Report */}
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                <h3 className="text-white font-semibold">AI Property Evaluation Report</h3>
                <button
                  onClick={generateAiReport}
                  disabled={generatingReport}
                  className="px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] disabled:opacity-50 transition-colors text-sm"
                >
                  {generatingReport ? 'Generating...' : aiReport ? 'Regenerate Report' : 'Generate AI Report'}
                </button>
                {aiReport && (
                  <button
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(`
                        <html>
                        <head>
                          <title>${property?.address} - Property Evaluation Report</title>
                          <style>
                            body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #1a1a1a; }
                            .header { border-bottom: 2px solid #00BCD4; padding-bottom: 16px; margin-bottom: 24px; }
                            .header .label { font-size: 11px; color: #888; letter-spacing: 2px; text-transform: uppercase; }
                            .header h1 { font-size: 24px; margin: 4px 0; }
                            .header .details { color: #555; font-size: 14px; }
                            .header .stats { margin-top: 8px; font-size: 13px; color: #00838F; }
                            .header .score { font-size: 28px; font-weight: bold; color: #00838F; margin-top: 8px; }
                            .section-header { font-size: 13px; font-weight: bold; color: #00838F; letter-spacing: 1px; border-bottom: 1px solid #00BCD4; padding-bottom: 4px; margin-top: 28px; margin-bottom: 12px; }
                            p { font-size: 14px; line-height: 1.7; color: #333; margin-bottom: 12px; }
                            .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ddd; font-size: 11px; color: #999; text-align: center; }
                            @media print { body { padding: 20px; } }
                          </style>
                        </head>
                        <body>
                          <div class="header">
                            <div class="label">SchemeSync RE — Property Evaluation</div>
                            <h1>${property?.address || ''}</h1>
                            <div class="details">${property?.city || ''}, ${property?.state || ''} ${property?.zip || ''}</div>
                            <div class="stats">${property?.beds || ''} bed · ${property?.baths || ''} bath · ${property?.sqft?.toLocaleString() || ''} sqft · $${property?.price ? Number(property.price).toLocaleString() : 'N/A'}</div>
                            <div class="score">${fitScore ? Math.round((fitScore.overall / 9) * 100) + '% — ' + fitScore.tier : ''}</div>
                          </div>
                          ${aiReport.split('\n').map(line => {
                            const trimmed = line.trim();
                            if (!trimmed) return '';
                            if (trimmed === trimmed.toUpperCase() && trimmed.length > 3 && !trimmed.startsWith('$'))
                              return '<div class="section-header">' + trimmed + '</div>';
                            return '<p>' + line + '</p>';
                          }).join('')}
                          <div class="footer">Generated by SchemeSync RE — AiDA Advisory LLC — ${new Date().toLocaleDateString()}</div>
                        </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.print();
                    }}
                    className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] transition-colors text-sm"
                  >
                    Print Report
                  </button>
                )}
              </div>
              {generatingReport ? (
                <div className="text-center py-12">
                  <div className="text-[#00D4FF] text-lg mb-2">Generating AI evaluation...</div>
                  <div className="text-gray-500 text-sm">Analyzing property data against buyer criteria</div>
                </div>
              ) : aiReport ? (
                <div className="bg-[#0A0E27] rounded-lg overflow-hidden">
                  {/* Report Header */}
                  <div className="p-4 md:p-6 border-b border-[#546E7A]/30">
                    <div className="text-[#B0BEC5] text-xs tracking-widest mb-1">SCHEMESYNC RE — PROPERTY EVALUATION</div>
                    <div className="text-white text-xl md:text-2xl font-bold">{property?.address}</div>
                    <div className="text-gray-400 text-sm">{property?.city}, {property?.state} {property?.zip}</div>
                    <div className="flex flex-wrap gap-3 md:gap-6 mt-3 text-sm">
                      {property?.beds && <span className="text-[#00D4FF]">{property.beds} bed</span>}
                      {property?.baths && <span className="text-[#00D4FF]">{property.baths} bath</span>}
                      {property?.sqft && <span className="text-[#00D4FF]">{property.sqft.toLocaleString()} sqft</span>}
                      {property?.price && <span className="text-[#00E676] font-bold">${Number(property.price).toLocaleString()}</span>}
                    </div>
                    {fitScore && (
                      <div className="mt-3 flex items-center gap-3">
                        <span className="text-2xl md:text-3xl font-bold text-white">{getScorePercent(fitScore.overall)}%</span>
                        <span className={`text-sm font-semibold ${getTierColor(fitScore.tier)}`}>{fitScore.tier}</span>
                      </div>
                    )}
                  </div>
                  {/* Report Body */}
                  <div className="p-4 md:p-6">
                    {aiReport.split('\n').map((line, i) => {
                      const trimmed = line.trim();
                      if (!trimmed) return <div key={i} className="h-3" />;
                      if (trimmed === trimmed.toUpperCase() && trimmed.length > 3 && !trimmed.startsWith('$') && !trimmed.startsWith('#')) {
                        return (
                          <div key={i} className="mt-6 mb-3 pb-1 border-b border-[#00D4FF]/30">
                            <span className="text-[#00D4FF] text-sm font-bold tracking-wide">{trimmed}</span>
                          </div>
                        );
                      }
                      return <p key={i} className="text-gray-300 text-sm leading-relaxed mb-3">{line}</p>;
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Click "Generate AI Report" to create a professional property evaluation.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: Grading */}
        {activeTab === 'grading' && (
          <div className="space-y-4">
            {/* Info Banner */}
            {user?.role === 'buyer' && (
              <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/20 p-4">
                <p className="text-gray-300 text-sm">
                  <span className="text-[#00D4FF] font-medium">Auto-Scored Grades</span> — These scores were calculated from listing data against your criteria.
                  After visiting a property, drag any slider to adjust the score based on your personal experience. Your overrides turn green. Click Reset to revert.
                </p>
              </div>
            )}

            {/* Progress Bar */}
            {progress && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Scoring Progress</span>
                    <span className="text-white font-medium">{progress.graded}/{progress.total} ({progress.percentage}%)</span>
                  </div>
                  <div className="w-full bg-[#546E7A]/30 rounded-full h-2">
                    <div className="bg-[#00D4FF] h-2 rounded-full transition-all" style={{ width: `${progress.percentage}%` }}></div>
                  </div>
                </div>
                {progress.is_complete && (
                  <span className="text-[#00E676] text-sm font-medium">Complete!</span>
                )}
              </div>
            )}

            {/* Criteria Cards */}
            {hierarchy.map(parent => (
              <div key={parent.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
                <div className="p-4 border-b border-[#546E7A]/20">
                  <h3 className="text-white font-semibold">{parent.name}
                    <span className="text-gray-500 text-sm font-normal ml-2">({(parent.weight * 100).toFixed(0)}% weight)</span>
                  </h3>
                </div>
                <div className="divide-y divide-[#546E7A]/10">
                  {parent.sub_criteria.map(sub => {
                    const existing = getExistingGrade(sub.id);
                    const override = overrides[sub.id];
                    const effectiveGrade = override ? override.grade : existing;
                    const autoPercent = existing ? Math.round((existing / 9) * 100) : null;
                    const overridePercent = override ? Math.round((override.grade / 9) * 100) : null;

                    return (
                      <div key={sub.id} className="px-4 py-3">
                        <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                          <div className="min-w-0">
                            <span className="text-gray-200 text-sm font-medium break-words">{sub.name}</span>
                            <span className="text-gray-500 text-xs ml-2">({(sub.weight * 100).toFixed(0)}%)</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 shrink-0">
                            {autoPercent !== null && (
                              <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${override ? 'bg-gray-600 line-through' : getGradeColor(existing)}`}>
                                Auto: {autoPercent}%
                              </span>
                            )}
                            {override && (
                              <span className="px-2 py-0.5 rounded text-xs font-bold text-white bg-green-600">
                                Yours: {overridePercent}%
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-500 text-xs mb-2">{sub.description}</p>

                        {/* Super Admin: direct grade buttons */}
                        {user?.role === 'super_admin' && (
                          <div className="flex flex-wrap gap-1.5">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(grade => (
                              <button
                                key={grade}
                                onClick={() => handleGrade(sub.id, grade)}
                                className={`w-9 h-8 rounded text-xs font-bold transition-all ${
                                  existing === grade
                                    ? `${getGradeColor(grade)} text-white ring-2 ring-white/50`
                                    : 'bg-[#0A0E27] text-gray-400 hover:bg-[#243B55] hover:text-white'
                                }`}
                              >
                                {grade}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Buyer: override slider */}
                        {user?.role === 'buyer' && (
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-6">0%</span>
                            <input type="range" min="1" max="9" step="1"
                              value={override ? override.grade : (existing || 5)}
                              onChange={(e) => handleOverride(sub.id, parseInt(e.target.value))}
                              className={`flex-1 h-2 ${override ? 'accent-[#00E676]' : 'accent-[#00D4FF]'}`} />
                            <span className={`text-sm font-bold w-12 text-right ${override ? 'text-[#00E676]' : 'text-[#00D4FF]'}`}>
                              {Math.round(((override ? override.grade : (existing || 5)) / 9) * 100)}%
                            </span>
                            {override && (
                              <button
                                onClick={() => removeOverride(sub.id)}
                                className="px-2 py-1 text-xs text-gray-500 hover:text-red-400 transition-colors whitespace-nowrap"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* TAB: Measurables */}
        {activeTab === 'measurables' && (
          <MeasurablesTab propertyId={propertyId} buyerId={buyerId} />
        )}
      </div>
    </div>
  );
}

// Measurables sub-component
function MeasurablesTab({ propertyId, buyerId }) {
  const [measurables, setMeasurables] = useState([]);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadMeasurables(); }, []);

  const loadMeasurables = async () => {
    try {
      const [entriesRes, scoreRes] = await Promise.all([
        authFetch(`${API}/api/measurables/entries/${buyerId}/${propertyId}`),
        authFetch(`${API}/api/measurables/score/${buyerId}/${propertyId}`),
      ]);
      if (entriesRes.ok) setMeasurables(await entriesRes.json());
      if (scoreRes.ok) setScore(await scoreRes.json());
    } catch (err) {
      console.error('Measurables error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-[#00D4FF] text-center py-8">Loading measurables...</div>;

  return (
    <div className="space-y-6">
      {/* Score Summary */}
      {score && score.has_score && (
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
          <h3 className="text-white font-semibold mb-4">Measurables Score</h3>
          <div className="flex items-center gap-4">
            <div className="text-3xl md:text-4xl font-bold text-white">{score.overall_pct}%</div>
            <div className="text-gray-400 text-sm">
              {score.scored_count} of {score.total_measurables} measurables scored
            </div>
          </div>
        </div>
      )}

      {/* Measurables Detail */}
      <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
        <h3 className="text-white font-semibold mb-4">Property Measurables</h3>
        {score && score.details && score.details.length > 0 ? (
          <div className="space-y-3">
            {score.details.map((d, i) => (
              <div key={i} className="flex flex-wrap justify-between items-center gap-2 p-3 bg-[#0A0E27] rounded-lg">
                <div className="min-w-0">
                  <span className="text-gray-200 text-sm font-medium break-words">{d.name}</span>
                  <span className="text-gray-500 text-xs ml-2">
                    (target: {d.operator} {d.target}{d.unit ? ` ${d.unit}` : ''})
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {d.actual !== null ? (
                    <>
                      <span className="text-white font-medium">{d.actual}{d.unit ? ` ${d.unit}` : ''}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${d.met ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}`}>
                        {d.met ? 'MET' : 'MISS'}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-600 text-sm">Not measured</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No measurables configured for this buyer. Add measurable definitions in the buyer's profile.</p>
        )}
      </div>
    </div>
  );
}
